import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { ArchiveClientSummary, OrdersArchiveService } from '@callcenter-app/service/orders-archive.service';
import { CurrentOrderPage, OrdersCurrentService } from '@callcenter-app/service/orders-current.service';
import { businessMessage } from '@callcenter-app/components/order-live/order-http-error';

/**
 * `workplace.service.ts` — the app-local HTTP client of `/api/vn/workplace/**` (stage 4; precedent: `orders-current.service.ts`) and the two reads of a client's data that the shell (the call band) and the
 * page (the workplace) both need, kept for a few seconds so that one call asks the server once.
 *
 * W1 `GET /call[?call_id=]` — the operator's own call (type, exact seconds, reservations); W2 `POST /client-lookup` — the client behind a phone, created by nobody. W2 is a POST so that the phone never gets into an
 * address and the access journal of the server. W3 `POST /call-links` — the link of the operator's talking call to an order (`CallLinkService` asks it).
 */

export const WORKPLACE_BASE = '/api/vn/workplace';

export type CallType = 'IN' | 'OUT' | 'CALLBACK';
export type ServerCallState = 'ringing' | 'talking' | 'finished';

/** W1: the call from THIS operator's point of view. Each of the seconds is there only in the state it means something in; a field the server has nothing for is absent. */
export interface WorkplaceCall {
  callId: string;
  state: ServerCallState;
  /** The call was answered at another number: `state` is `finished`, the call is not this operator's. */
  elsewhere: boolean;
  type?: CallType | null;
  phone: string;
  mobile: boolean;
  countryId?: number;
  ringSeconds?: number | null;
  talkSeconds?: number | null;
  talkDurationSeconds?: number | null;
  endedSecondsAgo?: number | null;
  cartIds?: number[] | null;
  orderIds?: number[] | null;
}

export interface WorkplaceCallResponse {
  call: WorkplaceCall | null;
}

/** A mark a client has in one brand: VIP, the black list, the note. */
export interface WorkplaceLabel {
  brandId: number;
  brandTitle?: string;
  vip: boolean;
  blacklisted: boolean;
  note?: string;
}

export interface WorkplaceClient {
  id: number;
  name?: string;
  /** The day the client was made (ISO date). */
  since?: string;
  birthDate?: string;
  legal: boolean;
  companyTitle?: string;
  callsLast30Days: number;
  labels?: WorkplaceLabel[];
}

/** W2: `client` is `null` for a number nobody has in the operator's countries — no error, the number may be a new client's. */
export interface WorkplaceClientResponse {
  phone: string;
  mobile: boolean;
  countryId?: number;
  client: WorkplaceClient | null;
}

/**
 * W3: what the server did. `linked` + `!already` — written now; `linked` + `already` — it was there; `!linked` + `!samePhone` — the numbers differ, nothing is written until the operator confirms
 * (`confirmDifferentPhone`). `orderNumber` is there in every answer (the window names the order by it).
 */
export interface WorkplaceCallLink {
  linked: boolean;
  already: boolean;
  samePhone: boolean;
  orderNumber?: string;
}

/** The client's summary of «Архив» as the page needs it: ready, the client has no finished order yet (`ordersArchive.clientNotFound`), or it could not be read. */
export type SummaryLoad =
  | { status: 'ok'; summary: ArchiveClientSummary }
  | { status: 'none' }
  | { status: 'error' };

/** How long what was read about a client is reused: the band and the page of ONE call ask the server once. */
export const CLIENT_CACHE_MS = 20_000;
/** The orders of a client in `/orders-current/list` (client mode) the page draws; the rest is in «Заказах». */
export const CLIENT_CURRENT_LIMIT = 50;

@Injectable({ providedIn: 'root' })
export class WorkplaceService {
  private cache = new Map<string, { at: number; read: Observable<unknown> }>();

  constructor(
    private http: HttpClient,
    private archive: OrdersArchiveService,
    private current: OrdersCurrentService,
  ) {
  }

  /** W1. Without `callId` — the call the operator's number has right now (talking, or ringing and not older than 20 minutes); with it — that call, when it is the operator's. */
  getCall(callId?: string): Observable<WorkplaceCallResponse> {
    let params = new HttpParams();
    if (callId) {
      params = params.set('call_id', callId);
    }
    return this.http.get<WorkplaceCallResponse>(`${WORKPLACE_BASE}/call`, { params });
  }

  /** W2. `phone`: 6 to 15 digits as typed or as the call carries it. */
  lookupClient(phone: string): Observable<WorkplaceClientResponse> {
    return this.http.post<WorkplaceClientResponse>(`${WORKPLACE_BASE}/client-lookup`, { phone });
  }

  /** W3. The call is the operator's own and talking (`workplace.callNotCurrent` otherwise); a reservation the call does not hold is `ordersArchive.orderNotFound`. A different phone needs `confirmDifferentPhone`. */
  linkCall(callId: string, orderId: number, confirmDifferentPhone = false): Observable<WorkplaceCallLink> {
    return this.http.post<WorkplaceCallLink>(`${WORKPLACE_BASE}/call-links`, { callId, orderId, ...(confirmDifferentPhone ? { confirmDifferentPhone: true } : {}) });
  }

  /** The summary of «Архива» of a client. Never fails: a client without a finished order is `none`, any other trouble is `error` (and is not remembered). */
  clientSummary(clientId: number, fresh = false): Observable<SummaryLoad> {
    return this.remembered<SummaryLoad>(`summary:${clientId}`, fresh, () => this.archive.getClientSummary(clientId).pipe(
      map((summary): SummaryLoad => ({ status: 'ok', summary })),
      catchError((err: HttpErrorResponse) => of<SummaryLoad>(businessMessage(err) === 'ordersArchive.clientNotFound' ? { status: 'none' } : { status: 'error' })),
    ), load => load.status === 'error');
  }

  /** The client's orders and reservations of `/orders-current/list` (client mode, not polled). A failure reaches the caller. */
  clientCurrent(clientId: number, fresh = false): Observable<CurrentOrderPage> {
    return this.remembered<CurrentOrderPage>(`current:${clientId}`, fresh, () => this.current.getList({ clientId, limit: CLIENT_CURRENT_LIMIT }));
  }

  /** What is remembered about a client is out of date (the name was saved, an order was made): the next read asks the server. */
  forgetClient(clientId: number): void {
    this.cache.delete(`summary:${clientId}`);
    this.cache.delete(`current:${clientId}`);
  }

  clearCache(): void {
    this.cache.clear();
  }

  private remembered<T>(key: string, fresh: boolean, load: () => Observable<T>, isFailure: (value: T) => boolean = () => false): Observable<T> {
    const hit = this.cache.get(key);
    if (!fresh && hit && performance.now() - hit.at < CLIENT_CACHE_MS) {
      return hit.read as Observable<T>;
    }
    const entry = { at: performance.now(), read: load().pipe(shareReplay({ bufferSize: 1, refCount: false })) };
    this.cache.set(key, entry);
    // a failed read is not kept: the next one asks again
    return (entry.read as Observable<T>).pipe(tap({
      next: value => {
        if (isFailure(value) && this.cache.get(key) === entry) {
          this.cache.delete(key);
        }
      },
      error: () => {
        if (this.cache.get(key) === entry) {
          this.cache.delete(key);
        }
      },
    }));
  }
}
